// Copyright (c) 2024, WSO2 LLC. (https://www.wso2.com). All Rights Reserved.
//
// This software is the property of WSO2 LLC. and its suppliers, if any.
// Dissemination of any information or reproduction of any material contained
// herein in any form is strictly forbidden, unless permitted by WSO2 expressly.
// You may not alter or remove any copyright or other notice from copies of this content.

import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  AppBar,
  Container,
  Grid,
  LinearProgress,
  Toolbar,
  Typography,
} from "@mui/material";
import { Box } from "@mui/system";
import { DocumentTypes, MinimalDocumentData } from "../utils/types";
import { publicPageTitles } from "../config/constant";
import Logo from "../assets/img/WSO2-Logo-Black.png";
import Footer from "../components/Footer";
import { CertificateService } from "../services/certificateService";

const AwardedCertificate = () => {
  let { certificationId, examToken } = useParams();
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (certificationId && examToken) {
      generateCertificatePDF(certificationId, examToken);
    }
  }, [certificationId, examToken]);

  const generateCertificatePDF = async (certificationId: string, certificateId: string) => {
    setLoading(true);
    setError(null);
    
    try {
      const documentData: MinimalDocumentData = {
        id: certificateId,
        displayName: "WSO2 Certification",
        contact: {
          certificationId: certificationId
        }
      };
       
      const response = await CertificateService.generateCertificatePDF(
        documentData as any, 
        DocumentTypes.CERTIFICATE
      );
      
      if (response.success && response.data) {
        const url = CertificateService.createPdfUrl(response.data);
        setPdfUrl(url);
      } else {
        setError(response.error || 'Failed to generate certificate');
      }
    } catch (err) {
      setError('Failed to generate certificate');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <AppBar position="fixed" color="inherit" elevation={2}>
        <Container disableGutters>
          <Toolbar>
            <img
              src={Logo}
              alt="Logo"
              style={{ width: "120px", marginLeft: "-24px", marginRight: "10px" }}
            />
            <Typography
              variant="h6"
              color="inherit"
              sx={{ flexGrow: 1 }}
              align="left"
            >
              {publicPageTitles.awardedCertificate}
            </Typography>
          </Toolbar>
        </Container>
      </AppBar>
      
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          minHeight: "100vh",
          justifyContent: "center",
        }}
      >
        <Container>
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              flexDirection: "column",
              width: "100%",
              pt: 10,
              pb: 3,
            }}
          >
            {loading ? (
              <div style={{ width: "25%", marginBottom: 200 }}>
                <LinearProgress color="secondary" />
              </div>
            ) : error ? (
              <>
                <img
                  src="/no-data.svg"
                  alt="404"
                  height={200}
                  style={{ marginTop: 50 }}
                />
                <Typography variant="h6" sx={{ color: "red", mt: 5 }}>
                  {error || "Failed to generate certificate"}
                </Typography>
              </>
            ) : pdfUrl ? (
              <iframe
                style={{ 
                  overflow: "hidden", 
                  width: "100%",
                  height: "70vh",
                }}
                src={`${pdfUrl}#toolbar=0&navpanes=0&scrollbar=0&view=fit`}
              />
            ) : null}
          </Box>
        </Container>
      </Box>
      
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          bottom: 0,
          overflow: "hidden",
        }}
      >
        <Footer />
      </Box>
    </>
  );
};

export default AwardedCertificate;
